import { useState } from "react";
import { ThumbsUp, ThumbsDown, Meh, Send, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const OPTIONS = [
  { value: "positive", label: "Positive", icon: ThumbsUp, color: "emerald", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-500", help: "Smooth trade — would do again." },
  { value: "neutral", label: "Neutral", icon: Meh, color: "amber", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-500", help: "Trade went through but with friction." },
  { value: "negative", label: "Negative", icon: ThumbsDown, color: "rose", bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-500", help: "Issues — others should be cautious." },
];

export default function RateUserModal({ open, onClose, offer, otherUserId, otherUserName, onRated }) {
  const [sentiment, setSentiment] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!sentiment) { toast.error("Pick a rating"); return; }
    if (comment.trim().length < 5) { toast.error("Add a short comment (5+ chars)"); return; }
    setBusy(true);
    try {
      await api.post("/ratings", {
        to_user_id: otherUserId,
        trade_offer_id: offer.id,
        sentiment,
        comment: comment.trim(),
      });
      toast.success("Thanks — your feedback helps the community");
      onRated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-0 md:p-6" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full md:max-w-lg md:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="rate-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E2E2]">
          <div>
            <h3 className="font-display text-lg font-semibold">Rate this trade</h3>
            <p className="text-xs text-zinc-500">Help others know what to expect from {otherUserName}.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F1F5F9]" data-testid="rate-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto">
          <div className="grid grid-cols-3 gap-2">
            {OPTIONS.map(o => {
              const Icon = o.icon;
              const active = sentiment === o.value;
              return (
                <button
                  key={o.value}
                  onClick={() => setSentiment(o.value)}
                  className={`rounded-2xl border-2 p-4 transition-all ${active ? `${o.border} ${o.bg}` : "border-[#E2E2E2] hover:border-zinc-300"}`}
                  data-testid={`rate-${o.value}`}
                >
                  <Icon className={`w-6 h-6 mx-auto ${active ? o.text : "text-zinc-400"}`} />
                  <p className={`text-sm font-medium mt-2 ${active ? o.text : "text-zinc-700"}`}>{o.label}</p>
                </button>
              );
            })}
          </div>
          {sentiment && (
            <p className="text-xs text-zinc-500 text-center -mt-2">{OPTIONS.find(o => o.value === sentiment)?.help}</p>
          )}

          <div>
            <label className="block text-sm font-medium mb-1.5">Comment <span className="text-rose-500">*</span></label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              className="input-text min-h-[100px] resize-y"
              placeholder="Share specifics — communication, item condition, meetup etc."
              data-testid="rate-comment"
            />
            <p className="text-[11px] text-zinc-400 mt-1">{comment.length} / 500</p>
          </div>
        </div>

        <div className="p-5 border-t border-[#E2E2E2] flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-pill btn-soft !text-sm !py-2">Cancel</button>
          <button onClick={submit} disabled={busy || !sentiment || comment.trim().length < 5} className="btn-pill btn-accent disabled:opacity-50 !text-sm !py-2" data-testid="rate-submit">
            <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit rating"}
          </button>
        </div>
      </div>
    </div>
  );
}
